import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from 'app/core/auth/auth.service';
import { getLocal, LOCAL_SESSION_KEY, setLocal } from 'global';
import { catchError, Observable, tap, throwError } from 'rxjs';
import { APP_VERSION } from 'version';

/**
 * Endpoints públicos del flujo de auth que NO requieren token (sign-in, sign-up,
 * forgot-password, etc.). Para todo lo demás, si no hay token la request debe
 * abortarse con error claro en lugar de salir cruda.
 */
function isPublicAuthEndpoint(url: string): boolean {
    return /\/(sign-in|sign-up|forgot-password|change-password|reset-password|app-config|health-check|confirmation-required)(\?|$)/.test(url);
}

/**
 * Intercept
 *
 * @param req
 * @param next
 */
export const authInterceptor = (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
    const authService = inject(AuthService);

    // Clone the request object
    let newReq = req.clone();

    if (newReq.url.includes('amazonaws.com')) {
        return next(newReq);
    }

    // Retrieve session and version
    const session = getLocal(LOCAL_SESSION_KEY);
    const version = APP_VERSION;

    if (authService.accessToken) {
        if (req.method.toUpperCase() === 'GET') {
            let params = req.params;

            if (version) params = params.append('version', version);
            if (session) params = params.append('session', session);

            newReq = req.clone({
                headers: req.headers.set('Authorization', 'Bearer ' + authService.accessToken),
                params: params,
            });
        } else if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(req.method.toUpperCase())) {
            // A diferencia de un objeto cualquiera JSON, el formData requiere un append para agregar propiedades. Por eso se hace esta verificación
            const headers = req.headers.set('Authorization', 'Bearer ' + authService.accessToken);
            if (req.body instanceof FormData) {
                // Agregar al FormData
                req.body.append('version', version);
                req.body.append('session', session);

                newReq = req.clone({ headers, body: req.body });
            } else {
                // Manejar otros tipos de cuerpos (ej. JSON)
                const body = typeof req.body === 'object' && req.body !== null ? { ...req.body } : {};
                body['version'] = version;
                body['session'] = session;

                newReq = req.clone({ headers, body });
            }
        }
    }
    // ROLLBACK 2026-06-30: el branch "abortar request si !accessToken" rompía
    // el boot de Angular porque hay requests legítimas sin token durante init
    // (/me boot check, /app-config, etc). Causaba loop signOut → location.reload
    // y pantalla en blanco en admin.motoboychile.app. El mensaje "0 Unknown Error"
    // del cliente original era un sintoma; el fix correcto va por otro lado.

    // Response handling
    return next(newReq).pipe(
        tap((event) => {
            if (event instanceof HttpResponse) {
                const session = event.headers.get('session') || event.body?.['session'];
                if (session) {
                    setLocal(LOCAL_SESSION_KEY, session);
                }
            }
        }),
        catchError((error) => {
            // Catch "401 Unauthorized" responses
            if (error instanceof HttpErrorResponse && error?.status === 401) {
                // Bug 2026-06-30: mensaje claro al usuario antes del logout en
                // lugar de hacer reload silencioso que dejaba al user perdido
                // viendo "0 Unknown Error" en la pantalla previa.
                try {
                    if (typeof window !== 'undefined') {
                        const msg = (error.error as any)?.message || 'Tu sesión ha expirado. Por favor inicia sesión nuevamente.';
                        // Guardamos el mensaje para que sign-in lo muestre al renderizar.
                        window.sessionStorage.setItem('mb_session_expired_msg', msg);
                    }
                } catch {
                    // sessionStorage podría estar deshabilitado (incognito strict / SSR).
                }

                // Sign out
                authService.signOut();

                // Reload the app
                location.reload();
            }

            return throwError(error);
        })
    );
};
